import type { JSX, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { NAV_ITEMS } from '@/layouts/navigation';
import { platformRoleLabel, type PlatformPermission } from '@/lib/permissions';

import styles from './AccessState.module.scss';

function SessionLoadError({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div className={styles.box} role="alert">
      <h1 className={styles.title}>No pudimos cargar tus permisos</h1>
      <p className={styles.text}>Revisa tu conexión e intenta de nuevo.</p>
      <button type="button" className={styles.link} onClick={onRetry}>
        Reintentar
      </button>
    </div>
  );
}

export function NoAccess({ role }: { role?: string }): JSX.Element {
  return (
    <div className={styles.box} role="alert">
      <h1 className={styles.title}>Sin acceso a esta sección</h1>
      <p className={styles.text}>
        {role ? `Tu rol (${platformRoleLabel(role)}) no incluye esta sección.` : 'Tu rol no incluye esta sección.'}{' '}
        Si la necesitas, pídesela a un administrador Super.
      </p>
    </div>
  );
}

interface Props {
  permission: PlatformPermission;
  children: ReactNode;
}

/**
 * OS-1717 — muestra la sección solo con la capacidad. Mientras se confirma no muestra nada que
 * dependa de ella; sin la capacidad, explica por qué en la misma URL (el API respondería 403).
 */
export function RequirePermission({ permission, children }: Props): JSX.Element {
  const { can, isLoading, isError, session, refetch } = usePlatformPermissions();
  if (isLoading) {
    return <p className={styles.text}>Cargando permisos…</p>;
  }
  if (isError) {
    return <SessionLoadError onRetry={refetch} />;
  }
  if (!can(permission)) {
    return <NoAccess role={session?.role} />;
  }
  return <>{children}</>;
}

/** OS-1717 — `/` lleva a la primera sección que el rol puede ver (SOPORTE AGENTE no tiene Dashboard). */
export function HomeRedirect(): JSX.Element {
  const { can, isLoading, isError, session, refetch } = usePlatformPermissions();
  if (isLoading) {
    return <p className={styles.text}>Cargando permisos…</p>;
  }
  if (isError) {
    return <SessionLoadError onRetry={refetch} />;
  }
  const first = NAV_ITEMS.find((item) => can(item.permission));
  return first ? <Navigate to={first.to} replace /> : <NoAccess role={session?.role} />;
}
